import type { FormEvent } from 'react'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { ColumnConfig } from '@/components/tables/enhanced-data-table'
import type {
  DepartmentDTO,
  ListDepartmentsParams,
  OrganisationMemberPayload,
  UserDetailsResponse,
} from '@/utils/api/user-service/models'

import { OrganisationSearchInput } from '@/components/common/OrganisationSearchInput'
import { UserSearchInput } from '@/components/common/UserSearchInput'
import SearchSelect from '@/components/search-select'
import { EnhancedDataTable } from '@/components/tables/enhanced-data-table'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useAuth } from '@/features/authentication/components/auth-context'
import { isAdminUser, isOrganisationAdminUser } from '@/lib/role-guards'
import { cn, handleError } from '@/lib/utils'
import {
  removeMember,
  useAddUserToDepartment,
  useAddUserToOrganisation,
  useGetAllOrganisations,
  useGetAllUserDetails,
  useGetMembers,
  useListDepartments,
  useRemoveMember,
} from '@/utils/api/user-service'

const DEFAULT_ADD_MEMBER_STATE = {
  userId: undefined as number | undefined,
  departmentId: '',
}

const DEFAULT_ASSIGN_STATE = {
  userId: undefined as number | undefined,
  departmentId: '',
}

function getMemberUserId(member: OrganisationMemberPayload): number | undefined {
  return (member as OrganisationMemberPayload & { userId?: number }).userId ?? member.id
}

export function DepartmentMembersPage() {
  const { user: currentUser, isLoading: isAuthLoading } = useAuth()
  const isAdmin = isAdminUser(currentUser)
  const isOrgAdmin = isOrganisationAdminUser(currentUser)
  const isStandardUser = !!currentUser?.roles?.includes('ROLE_USER') && !isAdmin && !isOrgAdmin
  const shouldSelectOrganisation = isAdmin && !isOrgAdmin
  const isSuperAdmin = shouldSelectOrganisation
  const restrictedDepartmentId = typeof currentUser?.userDepartmentId === 'number'
    ? currentUser.userDepartmentId
    : undefined

  const [selectedOrganisationId, setSelectedOrganisationId] = useState<number | null>(null)
  const [organisationSearch, setOrganisationSearch] = useState('all')
  // const [organisationPickerOpen, setOrganisationPickerOpen] = useState(false)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [addMemberState, setAddMemberState] = useState(DEFAULT_ADD_MEMBER_STATE)
  const [addMemberError, setAddMemberError] = useState<string | null>(null)
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [assignDialogMode, setAssignDialogMode] = useState<'assign' | 'change'>('assign')
  const [assignState, setAssignState] = useState(DEFAULT_ASSIGN_STATE)
  const [assignMemberLabel, setAssignMemberLabel] = useState('')
  const [assignError, setAssignError] = useState<string | null>(null)
  const [userDetailsById, setUserDetailsById] = useState<Record<number, UserDetailsResponse>>({})
  const [detailsRefreshKey, setDetailsRefreshKey] = useState(0)
  const [departmentFilter, setDepartmentFilter] = useState('all')
  const [selectedMembers, setSelectedMembers] = useState<OrganisationMemberPayload[]>([])
  const [bulkRemovePending, setBulkRemovePending] = useState(false)
  const [clearSelectionKey, setClearSelectionKey] = useState(0)
  const activeDepartmentFilter = isStandardUser && restrictedDepartmentId !== undefined
    ? String(restrictedDepartmentId)
    : departmentFilter

  const organisationsQuery = useGetAllOrganisations({
    query: {
      enabled: shouldSelectOrganisation,
      staleTime: 30_000,
    },
  })
  const organisations = useMemo(() => organisationsQuery.data?.data ?? [], [organisationsQuery.data])

  const selectedOrganisation = useMemo(
    () => organisations.find(org => org.id === selectedOrganisationId),
    [organisations, selectedOrganisationId],
  )

  const effectiveOrganisationId = shouldSelectOrganisation
    ? selectedOrganisationId ?? 0
    : currentUser?.mainOrgainisationId ?? 0

  const departmentsParams = useMemo<ListDepartmentsParams>(() => ({
    page: 0,
    size: 200,
  }), [])
  const departmentsQuery = useListDepartments(departmentsParams)
  const allDepartments = useMemo(() => departmentsQuery.data?.data?.content ?? [], [departmentsQuery.data])

  // Filter departments by selected organisation
  const departments = useMemo(() => {
    if (effectiveOrganisationId <= 0) {
      return []
    }
    return allDepartments.filter(dept => dept.organisationId === effectiveOrganisationId)
  }, [allDepartments, effectiveOrganisationId])

  const visibleDepartments = useMemo(() => {
    if (!isStandardUser || restrictedDepartmentId === undefined) {
      return departments
    }
    return departments.filter(dept => dept.id === restrictedDepartmentId)
  }, [departments, isStandardUser, restrictedDepartmentId])

  const departmentMap = useMemo(() => {
    const map = new Map<number, DepartmentDTO>()
    departments.forEach((department) => {
      if (typeof department.id === 'number') {
        map.set(department.id, department)
      }
    })
    return map
  }, [departments])

  const mapDepartmentToOption = (department: DepartmentDTO) => ({
    value: String(department.id ?? 0),
    id: department.id ?? 0,
    name: department.name ?? department.code ?? String(department.id ?? 0),
  })

  const membersQuery = useGetMembers(effectiveOrganisationId, {
    query: {
      enabled: effectiveOrganisationId > 0,
      staleTime: 15_000,
    },
  })
  const members = useMemo(() => membersQuery.data?.data ?? [], [membersQuery.data])
  const userDetailsMutation = useGetAllUserDetails()
  const lastDetailsKeyRef = useRef<string>('')

  /* eslint-disable react-hooks-extra/no-direct-set-state-in-use-effect */
  const clearUserDetails = useCallback(() => {
    setUserDetailsById({})
  }, [])

  const applyUserDetails = useCallback((nextMap: Record<number, UserDetailsResponse>) => {
    setUserDetailsById(nextMap)
  }, [])

  const resetOrganisationState = useCallback(() => {
    setAddDialogOpen(false)
    setAssignDialogOpen(false)
    setAddMemberState(DEFAULT_ADD_MEMBER_STATE)
    setAssignState(DEFAULT_ASSIGN_STATE)
    setAssignMemberLabel('')
    setAddMemberError(null)
    setAssignError(null)
    clearUserDetails()
    setDetailsRefreshKey(0)
    setDepartmentFilter('all')
    setSelectedMembers([])
    setClearSelectionKey(prev => prev + 1)
    lastDetailsKeyRef.current = ''
  }, [clearUserDetails])
  /* eslint-enable react-hooks-extra/no-direct-set-state-in-use-effect */
  useEffect(() => {
    resetOrganisationState()
  }, [effectiveOrganisationId, resetOrganisationState])
  const memberIds = useMemo(
    () => members
      .map((member) => {
        return getMemberUserId(member)
      })
      .filter((id): id is number => typeof id === 'number'),
    [members],
  )
  const memberIdsKey = useMemo(() => {
    if (memberIds.length === 0) {
      return ''
    }
    const sortedIds = [...memberIds].sort((a, b) => a - b)
    return sortedIds.join(',')
  }, [memberIds])

  useEffect(() => {
    const requestKey = memberIdsKey === '' ? '' : `${memberIdsKey}|${detailsRefreshKey}`
    if (requestKey === lastDetailsKeyRef.current) {
      return
    }
    lastDetailsKeyRef.current = requestKey

    if (memberIdsKey === '') {
      clearUserDetails()
      return
    }

    let cancelled = false
    const sortedIds = memberIdsKey
      .split(',')
      .map(id => Number(id))
      .filter(id => !Number.isNaN(id))
    userDetailsMutation
      .mutateAsync({ data: sortedIds })
      .then((response) => {
        // console.log("response0d:", response.data);
        // if (cancelled) {
        //   return
        // }
        // console.log("response:", response.data);
        const nextMap: Record<number, UserDetailsResponse> = {}
        response.data.forEach((user) => {
          // console.log("(typeof user.id === 'number'):", (typeof user.id));
          // console.log("user: ", user);
          if (typeof user.id === 'number') {
            nextMap[user.id] = user
          }
        })
        // console.log("nextMap:", nextMap);

        applyUserDetails(nextMap)
      })
      .catch((_error) => {
        if (!cancelled) {
          clearUserDetails()
        }
      })
    return () => {
      cancelled = true
    }
  }, [applyUserDetails, clearUserDetails, detailsRefreshKey, memberIdsKey, userDetailsMutation])
  // console.log("UserDetailsById: ", userDetailsById);

  const addMemberMutation = useAddUserToOrganisation()
  const assignDepartmentMutation = useAddUserToDepartment()
  const removeMemberMutation = useRemoveMember({
    mutation: {
      onSuccess: () => {
        void membersQuery.refetch()
      },
      onError: (e: import('axios').AxiosError) => handleError(e, 'Unable to remove member.'),
    },
  })

  const filteredOrganisations = useMemo(() => {
    const search = organisationSearch.trim().toLowerCase()
    if (!search || search === 'all') {
      return organisations
    }

    return organisations.filter((org) => {
      const name = typeof org.name === 'string' ? org.name.toLowerCase() : ''
      const id = typeof org.id === 'number' ? String(org.id) : ''
      return name.includes(search) || id.includes(search)
    })
  }, [organisationSearch, organisations])

  const canManageMembers = effectiveOrganisationId > 0
  const canAddMember = canManageMembers && addMemberState.userId !== undefined && !isSuperAdmin
  const canAssignDepartment = assignState.userId !== undefined && assignState.departmentId.trim() !== ''

  // const handleOrganisationSelect = useCallback((org: OrganisationPayload) => {
  //   if (typeof org.id !== 'number') {
  //     return
  //   }
  //   setSelectedOrganisationId(org.id)
  //   setOrganisationPickerOpen(false)
  //   setOrganisationSearch('')
  // }, [])

  const handleOpenAddDialog = useCallback(() => {
    setAddMemberState(DEFAULT_ADD_MEMBER_STATE)
    setAddMemberError(null)
    setAddDialogOpen(true)
  }, [])

  const handleAddDialogOpenChange = useCallback((open: boolean) => {
    setAddDialogOpen(open)
    if (!open) {
      setAddMemberState(DEFAULT_ADD_MEMBER_STATE)
      setAddMemberError(null)
    }
  }, [])

  const handleAssignDialogOpenChange = useCallback((open: boolean) => {
    setAssignDialogOpen(open)
    if (!open) {
      setAssignDialogMode('assign')
      setAssignState(DEFAULT_ASSIGN_STATE)
      setAssignMemberLabel('')
      setAssignError(null)
    }
  }, [])

  const handleOpenAssignDialog = useCallback((member: OrganisationMemberPayload, departmentId?: number) => {
    const memberUserId = getMemberUserId(member)
    if (typeof memberUserId !== 'number') {
      return
    }
    setAssignState({
      userId: memberUserId,
      departmentId: typeof departmentId === 'number' ? String(departmentId) : '',
    })
    setAssignDialogMode(typeof departmentId === 'number' ? 'change' : 'assign')
    setAssignMemberLabel(member.name ?? member.email ?? `User ${member.id}`)
    setAssignError(null)
    setAssignDialogOpen(true)
  }, [])

  const handleAddMemberSubmit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canAddMember || addMemberState.userId === undefined) {
      return
    }
    setAddMemberError(null)
    try {
      await addMemberMutation.mutateAsync({
        userId: addMemberState.userId,
        organisationId: effectiveOrganisationId,
      })
      if (addMemberState.departmentId.trim() !== '') {
        const departmentId = Number(addMemberState.departmentId)
        if (!Number.isNaN(departmentId)) {
          await assignDepartmentMutation.mutateAsync({
            userId: addMemberState.userId,
            departmentId,
          })
        }
      }
      setAddDialogOpen(false)
      setAddMemberState(DEFAULT_ADD_MEMBER_STATE)
      setDetailsRefreshKey(prev => prev + 1)
      void membersQuery.refetch()
    }
    catch (error) {
      setAddMemberError(error instanceof Error ? error.message : 'Unable to add member.')
    }
  }, [
    addMemberMutation,
    addMemberState.departmentId,
    addMemberState.userId,
    assignDepartmentMutation,
    canAddMember,
    effectiveOrganisationId,
    membersQuery,
  ])

  const handleAssignSubmit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canAssignDepartment || assignState.userId === undefined) {
      return
    }
    const departmentId = Number(assignState.departmentId)
    if (Number.isNaN(departmentId)) {
      return
    }
    setAssignError(null)
    try {
      await assignDepartmentMutation.mutateAsync({
        userId: assignState.userId,
        departmentId,
      })
      setAssignDialogOpen(false)
      setAssignState(DEFAULT_ASSIGN_STATE)
      setDetailsRefreshKey(prev => prev + 1)
      void membersQuery.refetch()
    }
    catch (error) {
      setAssignError(error instanceof Error ? error.message : 'Unable to assign department.')
    }
  }, [
    assignDepartmentMutation,
    assignState.departmentId,
    assignState.userId,
    canAssignDepartment,
    membersQuery,
  ])

  const selectedMemberIds = useMemo(() => {
    return selectedMembers
      .map(member => member.id)
      .filter((id): id is number => typeof id === 'number')
  }, [selectedMembers])
  const selectedMemberCount = selectedMemberIds.length
  const canBulkRemove = canManageMembers && selectedMemberCount > 0
  const bulkRemoveButtonLabel = bulkRemovePending
    ? 'Removing...'
    : selectedMemberCount > 0
      ? `Remove selected (${selectedMemberCount})`
      : 'Remove selected'
  const bulkRemoveDescription = selectedMemberCount === 0
    ? 'Select members to remove from the organisation.'
    : selectedMemberCount === 1
      ? 'This will remove 1 member from the organisation.'
      : `This will remove ${selectedMemberCount} members from the organisation.`

  const handleBulkRemove = useCallback(async () => {
    if (!canManageMembers || selectedMemberIds.length === 0 || bulkRemovePending) {
      return
    }
    setBulkRemovePending(true)
    try {
      const results = await Promise.allSettled(
        selectedMemberIds.map(memberId => removeMember(memberId)),
      )
      const failed = results.filter(result => result.status === 'rejected')
      if (failed.length > 0) {
        const firstFailure = failed[0]
        if (firstFailure.status === 'rejected') {
          handleError(firstFailure.reason as import('axios').AxiosError, 'Unable to remove selected members.')
        }
      }
      if (failed.length < results.length) {
        void membersQuery.refetch()
        setClearSelectionKey(prev => prev + 1)
      }
    }
    finally {
      setBulkRemovePending(false)
    }
  }, [bulkRemovePending, canManageMembers, membersQuery, selectedMemberIds])

  const filteredMembers = useMemo(() => {
    if (isStandardUser) {
      if (restrictedDepartmentId === undefined) {
        return []
      }
      return members.filter((member) => {
        const memberUserId = getMemberUserId(member)
        const departmentId = memberUserId !== undefined ? userDetailsById[memberUserId]?.userDepartmentId : undefined
        return departmentId === restrictedDepartmentId
      })
    }

    if (activeDepartmentFilter === 'all') {
      return members
    }

    return members.filter((member) => {
      const memberUserId = getMemberUserId(member)
      const departmentId = memberUserId !== undefined ? userDetailsById[memberUserId]?.userDepartmentId : undefined

      if (activeDepartmentFilter === 'unassigned') {
        return departmentId === undefined
      }

      const parsedFilter = Number(activeDepartmentFilter)
      if (Number.isNaN(parsedFilter)) {
        return true
      }

      return departmentId === parsedFilter
    })
  }, [activeDepartmentFilter, isStandardUser, members, restrictedDepartmentId, userDetailsById])

  const isRemovingMembers = removeMemberMutation.isPending || bulkRemovePending
  const departmentFilterOptions = useMemo(() => {
    const departmentOptions = visibleDepartments
      .filter(department => typeof department.id === 'number')
      .map(department => ({
        value: String(department.id),
        id: String(department.id),
        name: String(department.name ?? department.code ?? department.id),
      }))

    if (isStandardUser) {
      return departmentOptions
    }

    return [
      { value: 'all', id: 0, name: 'All departments' },
      { value: 'unassigned', id: -1, name: 'Unassigned' },
      ...departmentOptions,
    ]
  }, [isStandardUser, visibleDepartments])

  const columns = useMemo<ColumnConfig<OrganisationMemberPayload>[]>(() => ([
    {
      key: 'name',
      header: 'Name',
      accessor: row => row.name ?? row.email ?? '-',
      cell: value => (value ? String(value) : '-'),
    },
    {
      key: 'email',
      header: 'Email',
      cell: value => (value ? String(value) : '-'),
    },
    {
      key: 'role',
      header: 'Role',
      cell: (value) => {
        const roleValue = value ? String(value) : ''

        return roleValue
          ? <Badge variant="outline">{roleValue.replaceAll('_', ' ').replace('ROLE', '')}</Badge>
          : '-'
      },
    },
    {
      key: 'status',
      header: 'Status',
      cell: (value) => {
        const statusValue = value ? String(value) : ''
        const colorVariant = statusValue === 'ACTIVE' ? 'successOutline' : statusValue === 'PENDING' ? 'warningOutline' : 'destructiveOutline'

        return statusValue ? <Badge variant={colorVariant}>{statusValue}</Badge> : '-'
      },
    },
    {
      key: 'department',
      header: 'Department',
      sortable: false,
      filterable: false,
      cell: (_value, row) => {
        const memberUserId = getMemberUserId(row)
        const departmentId = memberUserId !== undefined ? userDetailsById[memberUserId]?.userDepartmentId : undefined
        const department = departmentId !== undefined ? departmentMap.get(departmentId) : undefined
        const departmentLabel = department?.name ?? department?.code ?? (departmentId ? `Dept ${departmentId}` : 'Unassigned')
        return (
          <span className={cn(!departmentId && 'text-muted-foreground')}>
            {departmentLabel}
          </span>
        )
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      sortable: false,
      filterable: false,
      cell: (_value, row) => {
        const memberUserId = getMemberUserId(row)

        const departmentId = memberUserId !== undefined ? userDetailsById[memberUserId]?.userDepartmentId : undefined
        const hasDepartment = departmentId !== undefined && departmentId !== null
        const canUpdateDepartment = memberUserId !== undefined
        return (
          <div className="flex justify-end space-x-1.5">
            {canUpdateDepartment
              ? (
                <Button
                  type="button"
                  variant={hasDepartment ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleOpenAssignDialog(row, hasDepartment ? departmentId : undefined)}
                >
                  {hasDepartment ? 'Change' : 'Assign'}
                </Button>
              )
              : null}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={typeof row.id !== 'number' || isRemovingMembers}
                >
                  Remove
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Remove member</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will remove
                    {' '}
                    {row.name ?? row.email ?? 'this member'}
                    {' '}
                    from the organisation.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    disabled={isRemovingMembers}
                    onClick={() => {
                      if (typeof row.id === 'number') {
                        removeMemberMutation.mutate({ memberId: row.id })
                      }
                    }}
                  >
                    {isRemovingMembers ? 'Removing...' : 'Remove member'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )
      },

    },
  ]), [departmentMap, handleOpenAssignDialog, isRemovingMembers, removeMemberMutation, userDetailsById])

  // const orgSelectionTitle = selectedOrganisation?.name
  //   ?? (selectedOrganisation?.id
  //     ? `Organisation ${selectedOrganisation.id}`
  //     : selectedOrganisationId
  //       ? `Organisation ${selectedOrganisationId}`
  //       : 'Select organisation')

  const membersDescription = shouldSelectOrganisation
    ? (selectedOrganisation?.name
      ? `${selectedOrganisation?.name} staff members`
      : (selectedOrganisationId
        ? `Organisation ${selectedOrganisationId}`
        : 'Select an organisation to manage its members.'))
    : (currentUser?.mainOrgainisation ?? 'Members for your organisation.')
  const tableData = canManageMembers ? filteredMembers : []
  const tableEmptyMessage = shouldSelectOrganisation && !canManageMembers
    ? 'Select an organisation to view members.'
    : 'No members found.'
  const assignDialogTitle = assignDialogMode === 'change' ? 'Change department' : 'Assign department'
  const assignDialogDescription = assignDialogMode === 'change'
    ? `Update department for ${assignMemberLabel || 'this member'}.`
    : `Assign a department to ${assignMemberLabel || 'this member'}.`
  const assignSubmitLabel = assignDialogMode === 'change' ? 'Change department' : 'Assign'

  return (
    <div className="space-y-6">
      <Dialog open={addDialogOpen} onOpenChange={handleAddDialogOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add organisation member</DialogTitle>
            <DialogDescription>Select a user and optionally assign a department.</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleAddMemberSubmit}>
            <UserSearchInput
              id="department-members-user"
              label="User"
              placeholder="Search user by email..."
              required
              value={addMemberState.userId}
              onValueChange={userId => setAddMemberState(prev => ({ ...prev, userId }))}
              disableOrganisationFilter
            />
            <div className="space-y-2">
              <SearchSelect
                id="department-members-department"
                label="Department"
                placeholder={departmentsQuery.isPending ? 'Loading departments...' : 'Select department'}
                value={addMemberState.departmentId}
                onValueChange={value => setAddMemberState(prev => ({ ...prev, departmentId: value }))}
                disabled={departmentsQuery.isPending || departmentsQuery.isError}
                options={visibleDepartments
                  .filter(department => typeof department.id === 'number')
                  .map(mapDepartmentToOption)}
              />
            </div>

            {addMemberError
              ? (
                <Alert variant="destructive">
                  <AlertTitle>Unable to add member</AlertTitle>
                  <AlertDescription>{addMemberError}</AlertDescription>
                </Alert>
              )
              : null}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleAddDialogOpenChange(false)}
                disabled={addMemberMutation.isPending || assignDepartmentMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!canAddMember || addMemberMutation.isPending || assignDepartmentMutation.isPending}>
                {addMemberMutation.isPending ? 'Adding...' : 'Add member'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={assignDialogOpen} onOpenChange={handleAssignDialogOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{assignDialogTitle}</DialogTitle>
            <DialogDescription>{assignDialogDescription}</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleAssignSubmit}>
            <div className="space-y-2">
              <SearchSelect
                id="assign-department"
                label="Department"
                placeholder={departmentsQuery.isPending ? 'Loading departments...' : 'Select department'}
                value={assignState.departmentId}
                onValueChange={value => setAssignState(prev => ({ ...prev, departmentId: value }))}
                disabled={departmentsQuery.isPending || departmentsQuery.isError}
                options={visibleDepartments
                  .filter(department => typeof department.id === 'number')
                  .map(mapDepartmentToOption)}
              />
            </div>

            {assignError
              ? (
                <Alert variant="destructive">
                  <AlertTitle>Unable to assign department</AlertTitle>
                  <AlertDescription>{assignError}</AlertDescription>
                </Alert>
              )
              : null}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleAssignDialogOpenChange(false)}
                disabled={assignDepartmentMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!canAssignDepartment || assignDepartmentMutation.isPending}>
                {assignDepartmentMutation.isPending ? 'Saving...' : assignSubmitLabel}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <CardTitle>Staff members</CardTitle>
            <CardDescription>{membersDescription}</CardDescription>
          </div>
          <Button type="button" onClick={handleOpenAddDialog} disabled={!canManageMembers || membersQuery.isPending || (isSuperAdmin && !selectedOrganisation)}>
            Add member
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {isAuthLoading
            ? (
              <p className="text-sm text-muted-foreground">Loading user context...</p>
            )
            : null}
          {!isAuthLoading && !canManageMembers && (isAdmin || isOrgAdmin)
            ? (
              <Alert>
                <AlertTitle>Select an organisation</AlertTitle>
                <AlertDescription>
                  Choose an organisation in the table filters to view and manage its members.
                </AlertDescription>
              </Alert>
            )
            : null}
          {shouldSelectOrganisation && organisationsQuery.isError
            ? (
              <Alert variant="destructive">
                <AlertTitle>Unable to load organisations</AlertTitle>
                <AlertDescription>{organisationsQuery.error?.message ?? 'Please refresh the page.'}</AlertDescription>
              </Alert>
            )
            : null}
          {!isAuthLoading && !canManageMembers && !isAdmin && !isOrgAdmin
            ? (
              <Alert variant="destructive">
                <AlertTitle>No organisation assigned</AlertTitle>
                <AlertDescription>Please contact support to link your account to an organisation.</AlertDescription>
              </Alert>
            )
            : null}

          {departmentsQuery.isError
            ? (
              <Alert variant="destructive">
                <AlertTitle>Unable to load departments</AlertTitle>
                <AlertDescription>{departmentsQuery.error?.message ?? 'Please refresh the page.'}</AlertDescription>
              </Alert>
            )
            : null}
          {membersQuery.isError
            ? (
              <Alert variant="destructive">
                <AlertTitle>Unable to load members</AlertTitle>
                <AlertDescription>{membersQuery.error?.message ?? 'Please refresh the page.'}</AlertDescription>
              </Alert>
            )
            : null}
          {userDetailsMutation.isError
            ? (
              <Alert variant="destructive">
                <AlertTitle>Unable to load member details</AlertTitle>
                <AlertDescription>{userDetailsMutation.error?.message ?? 'Please refresh the page.'}</AlertDescription>
              </Alert>
            )
            : null}

          {canManageMembers || shouldSelectOrganisation
            ? (
              <EnhancedDataTable
                data={tableData}
                columns={columns}
                loading={membersQuery.isPending || userDetailsMutation.isPending}
                emptyStateMessage={tableEmptyMessage}
                enableColumnFilters={false}
                enableExport={false}
                enableRowSelection={canManageMembers}
                onSelectedRowsChange={setSelectedMembers}
                clearSelectionTrigger={clearSelectionKey}
                customButtons={(
                  <div className="flex flex-wrap items-center gap-2 md:col-span-2">
                    {canManageMembers
                      ? (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              disabled={!canBulkRemove || isRemovingMembers}
                            >
                              {bulkRemoveButtonLabel}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove selected members</AlertDialogTitle>
                              <AlertDialogDescription>{bulkRemoveDescription}</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel disabled={bulkRemovePending}>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                disabled={!canBulkRemove || bulkRemovePending}
                                onClick={handleBulkRemove}
                              >
                                Remove members
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )
                      : null}
                    {shouldSelectOrganisation
                      ? (
                        <div className="space-y-2 md:col-span-1">
                          <OrganisationSearchInput
                            id="department-members-organisation"
                            label=""
                            placeholder="Search organisations..."
                            value={selectedOrganisationId !== null ? String(selectedOrganisationId) : 'all'}
                            options={[{ value: 'all', id: 0, name: 'All Organisations' }, ...filteredOrganisations.map((org, index) => ({
                              value: String(org.id ?? org.name ?? index),
                              id: org.id ?? index,
                              name: org.name ?? `Organisation ${org.id}`,
                            }))]}
                            onValueChange={(value) => {
                              const orgId = Number(value)
                              if (!Number.isNaN(orgId)) {
                                setSelectedOrganisationId(orgId)
                                setOrganisationSearch('')
                              }
                              else {
                                setSelectedOrganisationId(null)
                              }
                            }}
                            disabled={organisationsQuery.isPending || organisationsQuery.isError}
                          />

                          {/* <Popover open={organisationPickerOpen} onOpenChange={setOrganisationPickerOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                role="combobox"
                                aria-expanded={organisationPickerOpen}
                                className="min-w-[220px] justify-between"
                              >
                                <span className={cn('truncate', !selectedOrganisationId && 'text-muted-foreground')}>
                                  {orgSelectionTitle}
                                </span>
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-full p-0" align="start">
                              <Command shouldFilter={false}>
                                <CommandInput
                                  placeholder="Search organisations..."
                                  value={organisationSearch}
                                  onValueChange={setOrganisationSearch}
                                />
                                <CommandList>
                                  <CommandEmpty>
                                    {organisationsQuery.isLoading ? 'Loading organisations...' : 'No organisations found'}
                                  </CommandEmpty>
                                  {filteredOrganisations.length > 0 && (
                                    <CommandGroup>
                                      {filteredOrganisations.map((org, index) => (
                                        <CommandItem
                                          key={org.id ?? org.name ?? index}
                                          value={String(org.id ?? org.name ?? index)}
                                          onSelect={() => handleOrganisationSelect(org)}
                                        >
                                          <Check
                                            className={cn(
                                              'mr-2 h-4 w-4',
                                              org.id === selectedOrganisationId ? 'opacity-100' : 'opacity-0',
                                            )}
                                          />
                                          <div className="flex flex-col">
                                            <span className="font-medium">{org.name ?? `Organisation ${org.id}`}</span>
                                            {org.email ? <span className="text-xs text-muted-foreground">{org.email}</span> : null}
                                          </div>
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  )}
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover> */}
                        </div>
                      )
                      : null}

                    <div className="space-y-2 md:col-span-1">
                      <SearchSelect
                        placeholder="All departments"
                        value={activeDepartmentFilter}
                        label=""
                        onValueChange={(value) => {
                          if (isStandardUser) {
                            return
                          }
                          setDepartmentFilter(value)
                        }}
                        disabled={departmentsQuery.isPending || departmentsQuery.isError || isStandardUser}
                        options={departmentFilterOptions}
                      />
                    </div>
                  </div>
                )}
              />
            )
            : null}
        </CardContent>
      </Card>
    </div>
  )
}
